import { useCallback, useState } from 'react'
import debounce from 'lodash.debounce'
import threadsService from '@/app-domains/threads/threads.service'
import boxesService from '@/app-domains/boxes/boxes.service'
import labelsService from '@/app-domains/labels/labels.service'
import contactsService from '@/app-domains/contacts/contacts.service'
import './SearchForm.scoped.sass'
import store from '@/core/store'

const isOnContactsPage = () => window.location.pathname.startsWith('/contacts')

export function SearchForm () {
  const [searchValue, setSearchValue] = useState('')
  const [dropdownHidden, setDropdownHidden] = useState(false)
  const [isSelectingBox, setIsSelectingBox] = useState(false)
  const debouncedSearch = useCallback(debounce(async e => await setSearch(e), 0), [])
  const [results, setResults] = useState({ text: '', boxes: [], labels: [], contacts: [] })

  const onInput = (e) => {
    setSearchValue(e.target.value)
    debouncedSearch(e)
  }

  const setSearch = async (e) => {
    const value = e.target.value
    if (value && value.length > 0) {
      setDropdownHidden(false)
      store.commit('SIDE_MENU_SECONDARY:HIDE')
      if (isOnContactsPage()) {
        setResults({
          text: value,
          boxes: [],
          labels: [],
          contacts: contactsService.searchAllContacts(value) || []
        })
      } else {
        setResults({
          text: value,
          boxes: boxesService.findBoxesByQuery(value.toLowerCase()) || [],
          labels: labelsService.findLabelsByQuery(value.toLowerCase()) || [],
          contacts: []
        })
      }
    } else {
      setDropdownHidden(true)
      if (!isOnContactsPage()) threadsService.applyBaseFilter()
    }
  }

  const selectTextFilter = (value) => {
    threadsService.applyFilterFor('text', `Search: ${value}`, value.toLowerCase())
    setDropdownHidden(true)
  }

  const selectBox = (box) => {
    threadsService.applyFilterFor('Box', box.boxalias || box.boxname, box.composedid)
    setDropdownHidden(true)
  }

  const selectLabel = (label) => {
    threadsService.applyFilterFor('Label', label.name, label.composedid, false)
    setDropdownHidden(true)
  }

  const selectContact = (contact) => {
    contactsService.travelToContactViewAndFocusContact(contact)
    setSearchValue('')
    setDropdownHidden(true)
  }

  const onContactsPage = isOnContactsPage()

  const renderContactResults = () => {
    if (results.contacts.length === 0) {
      return <div className="results-area">
        <div className="results-area-title">No matching contacts</div>
      </div>
    }
    return <div className="results-area">
      <div className="results-area-title">Contacts</div>
      {results.contacts.map((contact) => (
        <div key={contact.id} className="results-area-button" onClick={() => selectContact(contact)}>
          {contactsService.buildContactFullname(contact) || contact.emails?.[0] || '-'}
          {contactsService.buildContactFullname(contact) && contact.emails?.[0] && (
            <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.85em' }}>{contact.emails[0]}</span>
          )}
        </div>
      ))}
    </div>
  }

  return <div className="search-input">
    <div className="search-container">
      <input
      placeholder={onContactsPage ? 'Search contacts ...' : 'Search here ...'}
      spellCheck="false"
      onInput={onInput}
      data-testid="input-search"
      value={searchValue}
      onFocus={() => setDropdownHidden(false)}
      onBlur={() => {
        if (!isSelectingBox) setDropdownHidden(true)
      }}
    />
      <button onClick={onInput}>
        <svg width="20" height="20" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M7.6 2.8a4.8 4.8 0 1 0 0 9.6 4.8 4.8 0 0 0 0-9.6ZM.4 7.6a7.2 7.2 0 1 1 13.068 4.171l5.78 5.78a1.2 1.2 0 0 1-1.696 1.697l-5.78-5.779A7.2 7.2 0 0 1 .4 7.6Z" fill="currentColor"/></svg>
      </button>
    </div>
    {!dropdownHidden && searchValue && <div className="search-dropdown" id='search-dropdown' onMouseEnter={() => setIsSelectingBox(true)} onMouseLeave={() => setIsSelectingBox(false)}>
      {onContactsPage
        ? renderContactResults()
        : <>
          <div className="results-area">
            <div className="results-area-title">
              Text
            </div>
            <div className="results-area-button" onClick={() => selectTextFilter(results.text)}>
              {results.text}
            </div>
          </div>
          {results.boxes.length > 0 && <div className="results-area">
            <div className="results-area-title">
              Boxes
            </div>
            {results.boxes.map((box, i) => {
              return <div key={i} className="results-area-button" onClick={() => selectBox(box)}>
                {box?.boxname}
              </div>
            })}
          </div>}
          {results.labels.length > 0 && <div className="results-area">
            <div className="results-area-title">
              Labels
            </div>
            {results.labels.map((label, i) => {
              return <div key={i} className="results-area-button" onClick={() => selectLabel(label)}>
                {label?.name}
              </div>
            })}
          </div>}
        </>}
    </div>}
  </div>
}
